import {DateFormat} from "../date_format"
import NumberFormatter from "../number_formatter"
import UserTime from "../user_time"
import messageResource from "../messageResource"
import {ReportGroupingLevel} from "./constants"
import * as StringUtils from "../util/StringUtils"
import UserNameFormat, {formatFmlWithCheck} from "../util/UserNameFormat"
import {unsafe_selectWithState} from '../redux/utils'
import {doesShowUsernameInReports} from '../administration/darSettings/selectors'
import {CURRENT_PRODUCT, Product} from 'constants'

export default class BaseReportFormatUtils {
    static _dateForHeaderFormat = new DateFormat(window.SystemSettings.dateFormat)
    static _dateForDataRowFormat = new DateFormat(window.SystemSettings.dateFormat + ", EEEE")
    static _dateWithYearFormat = new DateFormat("MMM dd, yyyy")
    static _weekForDataRowFormat = new DateFormat("MMM dd")
    static _monthForDataRowFormat = new DateFormat("MMMM yyyy")
    static _balanceNumberFormatter = new NumberFormatter(window.SystemSettings.decimalSeparator, window.SystemSettings.digitGroupingSymbol, 2, true)
    static _timeNumberFormatter = new NumberFormatter(window.SystemSettings.decimalSeparator, window.SystemSettings.digitGroupingSymbol, 2, false)

    static formatTime(time, timeFormat) {
        return time ? (timeFormat == TimeFormat.TIME ? UserTime.formatTimeWithDigitSeparator(time) : this._timeNumberFormatter.format(time / 60)) : "-"
    }

    static formatPureTime(time, timeFormat) {
        if (time == null)
            return "-";
        return (timeFormat == TimeFormat.TIME ? UserTime.formatTimeWithDigitSeparator(time) : this._timeNumberFormatter.format(time / 60));
    }

    static formatDifferenceTime(time, timeFormat) {
        var sign = time > 0 ? "+" : "";
        return sign + this.formatPureTime(time, timeFormat);
    }

    static formatTimeFormat(timeFormat) {
        return timeFormat === TimeFormat.DECIMAL ?
               messageResource.getEscapedMessage("time_format_selector.time_format_decimal", window.SystemSettings.decimalSeparator) :
               messageResource.getMessage("common.hh_mm");
    }

    static formatPercent(value) {
        if (value == null)
            return "-";
        return parseFloat(Math.round(value * 10) / 10).toFixed(1).toString() + "%";
    }

    static formatGroupingLevel(groupingLevel) {
        switch (groupingLevel) {
            case ReportGroupingLevel.USER:
                return messageResource.getMessage("common.Users");
            case ReportGroupingLevel.USER_GROUP:
                return messageResource.getMessage("common.User_Groups");
            case ReportGroupingLevel.TIME_ZONE_GROUP:
                return messageResource.getMessage("common.Time_Zone_Groups");

            case ReportGroupingLevel.DATE:
                return messageResource.getMessage("common.Days");
            case ReportGroupingLevel.WEEK:
                return messageResource.getMessage("common.Weeks");
            case ReportGroupingLevel.MONTH:
                return messageResource.getMessage("common.Months");

            case ReportGroupingLevel.CUSTOMER:
                return messageResource.getMessage("common.Customers");
            case ReportGroupingLevel.PROJECT:
                return messageResource.getMessage("common.Projects");
            case ReportGroupingLevel.TASK:
                return messageResource.getMessage("common.Tasks");

            case ReportGroupingLevel.PROJECT_NAME:
                return messageResource.getMessage("report.common.projects_by_name");
            case ReportGroupingLevel.TASK_NAME:
                return messageResource.getMessage("report.common.tasks_by_name");
            case ReportGroupingLevel.BILLING_TYPE:
                return messageResource.getMessage("report.common.billing_types");

            case ReportGroupingLevel.NONE:
                return "";
        }
    }

    static formatByName(namedBean) {
        return namedBean.name;
    }

    static makeGroupingPresentationFlags(groupingLevels) {
        var groupingPresentationFlags = {};

        if (_.contains(groupingLevels, ReportGroupingLevel.CUSTOMER)) {
            groupingPresentationFlags.customerIsShown = true;
        }

        if (_.contains(groupingLevels, ReportGroupingLevel.PROJECT)) {
            groupingPresentationFlags.projectIsShown = true;
        }

        return groupingPresentationFlags;
    }

    static formatTask(task, groupingPresentationFlags, isCSV) {
        var taskName = task.name;

        if (!groupingPresentationFlags.projectIsShown) {
            var project = task.project;
            var customer = project.customer;

            if (groupingPresentationFlags.customerIsShown)
                taskName += " (" + project.name + ")";
            else
                taskName += " (" + customer.name + ", " + project.name + ")";
        }

        if (!task.estimated && isCSV) {
            taskName += ' (' + messageResource.getMessage("report.budgeted_vs_actual.not_estimated_note") + ")";
        }

        return taskName;
    }

    static formatProject(project, groupingPresentationFlags) {
        if (groupingPresentationFlags.customerIsShown)
            return project.name;

        return project.name + " (" + project.customer.name + ")";
    }

    static formatUser(user) {
        if (user["id"] == OTHER_USERS_ENTITY_ID)
            return messageResource.getMessage("report.other_users");
        return this.getFormattedUser(user["firstName"], user["lastName"], user["middleInitial"], user["username"])
    }

    static formatUserAndTrim(user, maxLen) {
        if (user["id"] == OTHER_USERS_ENTITY_ID)
            return messageResource.getMessage("report.other_users");

        var fn = StringUtils.cutLongText(user["firstName"], maxLen);
        var ln = StringUtils.cutLongText(user["lastName"], maxLen);
        var mn = StringUtils.cutLongText(user["middleInitial"], maxLen);
        var un = StringUtils.cutLongText(user["username"], maxLen);
        return this.getFormattedUser(fn, ln, mn, un)
    }

    static formatUserGroup(group) {
        return group.id !== USERS_WITHOUT_GROUP_ID ? group.name : messageResource.getMessage("report.user_groups.default_group_name");
    }

    static formatTimeZoneGroup(group) {
        return group.id !== DEFAULT_TIME_ZONE_GROUP_ID ? group.name : group.name + " " + messageResource.getMessage("common.default_group");
    }

    static formatDateForHeader(date) {
        return this._dateForHeaderFormat.formatDate(date)
    }

    static formatDateForDataRow(date) {
        return this._dateForDataRowFormat.formatDate(date)
    }

    static formatWeekForDataRow(week) {
        var dateRange = {
            from: Date.parseJSONDate(week.from),
            to: Date.parseJSONDate(week.to)
        };
        // look at TimeUtils.java#formatDateRange
        if (dateRange.from.getFullYear() == dateRange.to.getFullYear()) {
            // check if interval contains only one day
            if (dateRange.from.getMonth() == dateRange.to.getMonth() && dateRange.from.getDate() == dateRange.to.getDate())
                return this._dateWithYearFormat.formatDate(dateRange.from);
            return this._weekForDataRowFormat.formatDate(dateRange.from) + " - " + this._dateWithYearFormat.formatDate(dateRange.to);
        }

        return this._dateWithYearFormat.formatDate(dateRange.from) + " - " + this._dateWithYearFormat.formatDate(dateRange.to);
    }

    static formatMonthForDataRow(date) {
        return this._monthForDataRowFormat.formatDate(date)
    }

    static getGroupingLevelTitle(groupingLevel, identity, store, maxLen) {
        switch (groupingLevel) {
            case ReportGroupingLevel.DATE:
                return this.formatDateForDataRow(Date.parseJSONDate(store.getDateInfo(identity).date));
            case ReportGroupingLevel.WEEK:
                return this.formatWeekForDataRow(store.getWeek(identity));
            case ReportGroupingLevel.MONTH:
                return this.formatMonthForDataRow(Date.parseJSONDate(store.getMonth(identity).date));
            case ReportGroupingLevel.USER:
                if (maxLen === undefined)
                    return this.formatUser(store.getUser(identity));
                return this.formatUserAndTrim(store.getUser(identity), maxLen);
            case ReportGroupingLevel.USER_GROUP:
                return this.formatUserGroup(store.getUserGroup(identity));
            case ReportGroupingLevel.TIME_ZONE_GROUP:
                return this.formatTimeZoneGroup(store.getTimeZoneGroup(identity));
            case ReportGroupingLevel.PROJECT_NAME:
                return BaseReportFormatUtils.formatByName(store.getProjectName(identity));
            case ReportGroupingLevel.TASK_NAME:
                return BaseReportFormatUtils.formatByName(store.getTaskName(identity));
            case ReportGroupingLevel.BILLING_TYPE:
                return BaseReportFormatUtils.formatByName(store.getTypeOfWork(identity));
            case ReportGroupingLevel.PROJECT:
                return this.formatProject(store.getProject(identity), store.getGroupingPresentationFlags());
            case ReportGroupingLevel.CUSTOMER:
                return BaseReportFormatUtils.formatByName(store.getCustomer(identity));
            case ReportGroupingLevel.TASK:
                return this.formatTask(store.getTask(identity), store.getGroupingPresentationFlags());
        }
    }

    static getFormattedUser(firstName, lastName, middleInitial, username) {
        const showUsername = CURRENT_PRODUCT === Product.actiTIME ? unsafe_selectWithState((state) => doesShowUsernameInReports(state)) : false
        return showUsername ? UserNameFormat.formatLFMU(firstName, lastName, middleInitial, username)
            : UserNameFormat.formatLFM(firstName, lastName, middleInitial)
    }

    static reportUserFormatterWithUsernameIfNeeded = {
        format: (user) => BaseReportFormatUtils.getFormattedUser(user.firstName, user.lastName, user.middleInitial, user.username)
    }

    static reportUserFormatterWithoutUsername = {
        format: (user) => formatFmlWithCheck(user)
    }
}



// WEBPACK FOOTER //
// ./web/js/reports/FormatUtils.js